import { z } from 'zod';

export const MultiLangStringSchema = z.object({
  th: z.string(),
  en: z.string(),
  zh: z.string().default(''),
  ru: z.string().optional(),
  ja: z.string().optional(),
  ko: z.string().optional(),
});
export type MultiLangString = z.infer<typeof MultiLangStringSchema>;

export const HousekeepingItemConfigSchema = z.object({
  id: z.string(),
  icon: z.string().default('🧹'),
  name: MultiLangStringSchema,
  description: MultiLangStringSchema.optional(),
  maxQuantity: z.number().default(5),
  isActive: z.boolean().default(true),
});
export type HousekeepingItemConfig = z.infer<typeof HousekeepingItemConfigSchema>;

export const MaintenanceCategoryConfigSchema = z.object({
  id: z.string(),
  icon: z.string().default('🔧'),
  name: MultiLangStringSchema,
  description: MultiLangStringSchema.optional(),
  isActive: z.boolean().default(true),
});
export type MaintenanceCategoryConfig = z.infer<typeof MaintenanceCategoryConfigSchema>;

export const GuestServiceConfigSchema = z.object({
  housekeepingItems: z.array(HousekeepingItemConfigSchema),
  maintenanceCategories: z.array(MaintenanceCategoryConfigSchema),
  updatedAt: z.string().optional(),
});
export type GuestServiceConfig = z.infer<typeof GuestServiceConfigSchema>;

export const DEFAULT_HOUSEKEEPING_ITEMS: HousekeepingItemConfig[] = [
  {
    id: 'pillow',
    icon: '🛏️',
    name: { th: 'หมอนเพิ่ม', en: 'Extra Pillow', zh: '额外枕头', ru: 'Дополнительная подушка', ja: '追加の枕', ko: '추가 베개' },
    maxQuantity: 4,
    isActive: true,
  },
  {
    id: 'blanket',
    icon: '🛌',
    name: { th: 'ผ้าห่มเพิ่ม', en: 'Extra Blanket', zh: '额外毯子', ru: 'Дополнительное одеяло', ja: '追加の毛布', ko: '추가 담요' },
    maxQuantity: 2,
    isActive: true,
  },
  {
    id: 'towel',
    icon: '🧺',
    name: { th: 'ผ้าเช็ดตัวเพิ่ม', en: 'Extra Towels', zh: '额外毛巾', ru: 'Дополнительные полотенца', ja: '追加のタオル', ko: '추가 수건' },
    maxQuantity: 4,
    isActive: true,
  },
  {
    id: 'toiletries',
    icon: '🧴',
    name: { th: 'อุปกรณ์อาบน้ำ & สบู่', en: 'Toiletries & Amenities', zh: '洗发水及沐浴露', ru: 'Туалетные принадлежности', ja: 'バスアメニティ', ko: '욕실 용품' },
    maxQuantity: 5,
    isActive: true,
  },
  {
    id: 'water',
    icon: '💧',
    name: { th: 'น้ำดื่ม', en: 'Drinking Water', zh: '饮用水', ru: 'Питьевая вода', ja: '飲料水', ko: '생수' },
    maxQuantity: 6,
    isActive: true,
  },
  {
    id: 'dental_kit',
    icon: '🪥',
    name: { th: 'ชุดแปรงสีฟัน', en: 'Dental Kit', zh: '牙具套装', ru: 'Зубной набор', ja: '歯ブラシセット', ko: '칫솔 세트' },
    maxQuantity: 4,
    isActive: true,
  },
  {
    id: 'coffee_tea',
    icon: '☕',
    name: { th: 'ชา & กาแฟ', en: 'Coffee & Tea Set', zh: '咖啡及茶包', ru: 'Набор чая и кофе', ja: 'コーヒー・紅茶セット', ko: '커피 및 차 세트' },
    maxQuantity: 4,
    isActive: true,
  },
  {
    id: 'slippers',
    icon: '🩴',
    name: { th: 'รองเท้าสลิปเปอร์', en: 'Room Slippers', zh: '一次性拖鞋', ru: 'Тапочки', ja: 'スリッパ', ko: '실내화' },
    maxQuantity: 4,
    isActive: true,
  },
];

export const DEFAULT_MAINTENANCE_CATEGORIES: MaintenanceCategoryConfig[] = [
  {
    id: 'ac',
    icon: '❄️',
    name: { th: 'เครื่องปรับอากาศ / แอร์', en: 'Air Conditioner', zh: '空调问题', ru: 'Кондиционер', ja: 'エアコンの不具合', ko: '에어컨 문제' },
    isActive: true,
  },
  {
    id: 'plumbing',
    icon: '🚿',
    name: { th: 'ระบบประปา / น้ำอุ่น / ท่อตัน', en: 'Plumbing & Hot Water', zh: '水管及热水问题', ru: 'Сантехника и горячая вода', ja: '配管・給湯の不具合', ko: '배관 및 온수 문제' },
    isActive: true,
  },
  {
    id: 'lighting',
    icon: '💡',
    name: { th: 'หลอดไฟ / ระบบไฟฟ้า', en: 'Lighting & Electrical', zh: '灯光及电路问题', ru: 'Освещение и электрика', ja: '照明・電気の不具合', ko: '조명 및 전기 문제' },
    isActive: true,
  },
  {
    id: 'door_lock',
    icon: '🔑',
    name: { th: 'ประตูห้อง / คีย์การ์ด', en: 'Door Lock & Keycard', zh: '门锁及房卡问题', ru: 'Дверной замок и ключ-карта', ja: 'ドアロック・カードキーの不具合', ko: '도어락 및 키카드 문제' },
    isActive: true,
  },
  {
    id: 'tv_wifi',
    icon: '📺',
    name: { th: 'ทีวี & อินเทอร์เน็ต Wi-Fi', en: 'TV & Wi-Fi Internet', zh: '电视及网络问题', ru: 'ТВ и Wi-Fi интернет', ja: 'テレビ・Wi-Fiの不具合', ko: 'TV 및 Wi-Fi 인터넷 문제' },
    isActive: true,
  },
  {
    id: 'other',
    icon: '🔧',
    name: { th: 'ปัญหาหรือสิ่งชำรุดอื่นๆ', en: 'Other Repairs / Issues', zh: '其他维修问题', ru: 'Другие неисправности', ja: 'その他の修理・要望', ko: '기타 수리 및 요청' },
    isActive: true,
  },
];
